import { usePageHeader } from "contexts/PageHeaderContext";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import {
  Wallet,
  Plus,
  Search,
  // DollarSign,
  // Check,
  Clock,
  Download,
  ChevronUp,
  ChevronDown,
  Edit2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';
import useDebounce from 'hooks/useDebounce';
import { toast } from 'sonner';
import { FullScreenLoading } from 'components/ui/loader';
import { Loan } from 'types/qarzanHassana';
import Pagination from 'ui/pagination';
import { getQarzanHasanaList, getQarzanHasanaStatics } from 'services/qarzanApplicationService';
// import { useScreenPermission } from 'hooks/useScreenPermission';

interface QarzLoanRegisterPageProps {
  onNavigate: (page: string, loanId?: string) => void;
}

interface QarzanStatics {
  activeLoans: number;
  pendingApproval: number;
  totalApplications: number;
  pending: number;
  draft: number
}

export function QarzLoanRegisterPage({ onNavigate }: QarzLoanRegisterPageProps) {
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: "Applications",
      childTitle: "Qardan Hasana application management system",
      icon: <Wallet className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showStatistics, setShowStatistics] = useState(false);
  const [loans, setLoans] = useState<Loan[]>([])
  const [statics, setStatics] = useState<QarzanStatics | null>(null)
  const [loanTotalCount, setLoanTotalCount] = useState(0)
  // const { canAdd, canEdit, canDelete, screenName } = useScreenPermission();

  const [filters, setFilters] = useState({
    pageNo: 1,
    pageSize: 20,
  });

  const mergedFilters = useMemo(
    () => ({
      ...filters,
      searchTerm,
    }),
    [filters, searchTerm],
  );

  const debouncedFilters = useDebounce(mergedFilters, 400);

  // USEEFFECT FUNCTION START
  const getQarzanStatics = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getQarzanHasanaStatics();
      setStatics(data)
    } catch {
      toast.error("Failed to fetch draft visa applications");
    } finally {
      setLoading(false);
    }
  }, []);

  const stats = [
    {
      label: "Active Loans",
      value: statics?.totalApplications ?? 0,
      icon: Wallet,
      bgColor: "bg-blue-50",
      iconColor: "text-blue-600",
      borderColor: "border-blue-200",
    },
    {
      label: "Pending Approval",
      value: statics?.pending ?? 0,
      icon: Clock,
      bgColor: "bg-amber-50",
      iconColor: "text-amber-600",
      borderColor: "border-amber-200",
    },
    // {
    //   label: 'Total Disbursed',
    //   value: statics?.draft ?? 0,
    //   // icon: DollarSign,
    //   bgColor: 'bg-green-50',
    //   iconColor: 'text-green-600',
    //   borderColor: 'border-green-200'
    // }
    // {
    //   label: 'Recovered',
    //   value: 'PKR 1.8L',
    //   icon: Check,
    //   bgColor: 'bg-green-50',
    //   iconColor: 'text-green-600',
    //   borderColor: 'border-green-200'
    // },
  ];

  const fetchQarzanHasanaList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getQarzanHasanaList(debouncedFilters);
      setLoans(data?.items || [])
      setLoanTotalCount(data?.total || 0);
    } catch {
      toast.error("Failed to fetch draft visa applications");
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  useEffect(() => {
    fetchQarzanHasanaList()
    getQarzanStatics()
  }, [fetchQarzanHasanaList, getQarzanStatics]);



  const getStatusBadgeClass = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'POSTED':
        return 'bg-green-100 text-green-700 border border-green-300';
      case 'DRAFT':
        return 'bg-red-100 text-red-700 border border-red-300';
      default:
        return 'bg-gray-100 text-gray-700 border border-gray-300';
    }
  };


  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };


  const sortedLoanApplicants = useMemo(() => {
    if (!sortColumn) return loans;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [...loans].sort((a: any, b: any) => {
      const aValue = String(a[sortColumn] ?? "");
      const bValue = String(b[sortColumn] ?? "");

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const comparison = String(aValue).localeCompare(
        String(bValue),
        undefined,
        { numeric: true, sensitivity: "base" }
      );

      return sortDirection === "asc"
        ? comparison
        : -comparison;
    });
  }, [loans, sortColumn, sortDirection]);


  return (
    <div className="space-y-6 pb-8">
      {loading && <FullScreenLoading />}
      {/* Header */}
      <div className="flex items-center justify-end">


        <div className='flex gap-4'>

          <Button
            variant="outline"
            onClick={() => setShowStatistics(!showStatistics)}
            className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
          >
            {showStatistics ? (
              <ChevronUp className="w-4 h-4 mr-2" />
            ) : (
              <ChevronDown className="w-4 h-4 mr-2" />
            )}
            Statistics
          </Button>

          {/* {canAdd && ( */}
          <Button
            onClick={() => onNavigate('qardan-application/create')}
            style={{ background: '#10b981' }}
            className="text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Application
          </Button>
          {/* )} */}

        </div>
      </div>

      {/* Statistics Cards */}
      {showStatistics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className={`${stat.borderColor} ${stat.bgColor}/50`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-xs ${stat.iconColor} uppercase font-semibold`}>{stat.label}</p>
                      <p className={`text-2xl font-bold ${stat.iconColor}`}>{stat.value}</p>
                    </div>
                    <div className={`w-10 h-10 rounded-full ${stat.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${stat.iconColor}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}


      {/* Search */}
      <Card className="border-[#5B9BD5]/30">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search by application no, employee name, ITS, or purpose..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Applications Table */}
      <Card className="border-[#5B9BD5]/30">
        <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50">
          <CardTitle style={{ color: '#5B9BD5' }}>Applications List</CardTitle>
          <CardDescription>
            {loans.length} application{loans.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow style={{ backgroundColor: '#E3F2FD' }}>
                <TableHead
                  style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs"
                  onClick={() => handleSort('applicationNumber')}
                >
                  Application No
                  <div className="flex items-center gap-1">
                    Code
                    {sortColumn === 'applicationNumber' ? (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-30" />
                    )}
                  </div>
                </TableHead>
                <TableHead
                  style={{ color: '#5B9BD5' }}
                  className="px-4 py-3 text-xs"
                  onClick={() => handleSort('employeeName')}
                >
                  <div className="flex items-center gap-1">
                  Employee
                    {sortColumn === 'employeeName' ? (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-30" />
                    )}
                  </div>
                </TableHead>
                <TableHead
                  style={{ color: '#5B9BD5' }}
                  className="px-4 py-3 text-xs"
                  onClick={() => handleSort('qarzanAmount')}
                >
                  <div className="flex items-center gap-1">
                  Amount
                    {sortColumn === 'qarzanAmount' ? (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-30" />
                    )}
                  </div>
                </TableHead>
                <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">Purpose</TableHead>
                <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">EMI</TableHead>
                {/* <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">Remaining</TableHead> */}
                <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">Status</TableHead>
                <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    No applications found
                  </TableCell>
                </TableRow>
              ) : (
                loans && sortedLoanApplicants?.map((loan, i) => (
                  <TableRow key={loan.id || loan.publicId} className="hover:bg-gray-50">
                    <TableCell className="px-4 py-3 text-xs font-mono text-[#5B9BD5] font-medium">{loan?.applicationNumber}</TableCell>
                    <TableCell className="px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{loan.employeeName ?? "-"}</div>
                        <div className="text-xs text-gray-500 font-mono">{loan.itsNumber ?? "-"}</div>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm font-bold text-gray-900">${loan.qarzanAmount ?? "-"}</TableCell>
                    <TableCell className="px-4 py-3 text-xs text-gray-600">{loan.qarzanTypeName ?? "-"}</TableCell>
                    <TableCell className="px-4 py-3 text-xs font-medium text-gray-900">
                      {/* {loan.installments > 0 ? `$${loan.installments}` : '-'} */}
                      {loan.installments > 0 ? `${loan.installments}` : '-'}
                    </TableCell>
                    {/* <TableCell className="px-4 py-3 text-sm font-bold text-red-600">
                      {loan.remaining > 0 ? `$${loan.remaining}` : '-'}
                    </TableCell> */}
                    <TableCell className="px-4 py-3 text-xs">
                      <Badge className={getStatusBadgeClass(loan.status)}>
                        {loan.status}
                      </Badge>
                    </TableCell>

                    <TableCell className="px-4 py-3 text-xs text-right gap-2 flex justify-end">
                      {/* Show Post + File button only when status is Draft */}
                      {/* {loan.status?.toUpperCase() === "DRAFT" && ( */}
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onNavigate(`/qardan-application/edit/${loan.publicId}`)}
                          className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                          title="Edit application"
                        >
                          <Edit2 className="w-3.5 h-3.5 mr-1" />
                        </Button>
                      </>
                      {/* )} */}

                      {/* Show Download button only when status is Posted */}
                      {/* {loan.status?.toUpperCase() === "POSTED" && ( */}
                      <Button
                        size="sm"
                        variant="ghost"
                        // onClick={() => handleDownloadClick(receipt)}
                        className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                        title="Download receipt"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      {/* )} */}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {/* PAGINATION COMPONENT FOR DRAFT */}
          <Pagination
            totalItems={loanTotalCount}
            itemsPerPage={filters.pageSize}
            currentPage={filters.pageNo}
            onPageChange={(page) =>
              setFilters(prev => ({
                ...prev,
                pageNo: page,
              }))
            }
            onPageSizeChange={(size) =>
              setFilters((prev) => ({
                ...prev,
                pageSize: size,
                pageNo: 1,
              }))
            }
            label="qardan"
          />
        </CardContent>
      </Card>
    </div>
  );
}
